/**
 * ViewExportContext — the seam that lets a non-board representation publish its
 * data-export actions (CSV, …) up to the shared {@link ViewReprChrome} toolbar,
 * which has no knowledge of the active view's data. The active container calls
 * `useRegisterViewExport(actions)`; the chrome reads them via
 * `useViewExportActions()` and renders the Export menu (nothing when empty, e.g.
 * the flow view). The index keeps its OWN export (beside its search box) — this
 * serves the headerless tabular surfaces (table / matrix / grid).
 */
import { createContext, useContext, useEffect } from "react";
import type { ViewExportAction } from "./export/ViewExportMenu.js";

export interface ViewExportContextValue {
  readonly actions: readonly ViewExportAction[];
  readonly setActions: (actions: readonly ViewExportAction[]) => void;
}

export const ViewExportContext = createContext<ViewExportContextValue | null>(null);

/** The chrome side: the currently-published export actions (empty when none). */
export function useViewExportActions(): readonly ViewExportAction[] {
  return useContext(ViewExportContext)?.actions ?? [];
}

/**
 * The container side: publish this surface's export actions while mounted, and
 * clear them on unmount. Pass a STABLE (memoized) `actions` array — it's an
 * effect dependency, so a fresh array each render would loop.
 */
export function useRegisterViewExport(actions: readonly ViewExportAction[]): void {
  const ctx = useContext(ViewExportContext);
  const setActions = ctx?.setActions;
  useEffect(() => {
    if (!setActions) return;
    setActions(actions);
    return () => setActions([]);
  }, [setActions, actions]);
}
